// v0.40 — identity emoji. A friendly per-wallet mark, NOT a security feature.
//
// Auto-assigned by a trivial stable index (same principle as the address index)
// over the wallet NAME — the name is on the device flag before login, so the
// same emoji shows on the lock screen and in-app, on any browser, with no
// address and no server. The user can override it (tap in the Wallet); the
// override is remembered on this device and wins over the default.

export const EMOJI_SET = [
  // The DEFAULT emoji is hash % DEFAULT_POOL over the first DEFAULT_POOL entries
  // (v0.60.20) — so those 60 positions are part of existing wallets' identity:
  // replace-in-place only, never insert/remove/reorder. Everything after
  // DEFAULT_POOL is pick-only and can grow freely.
  // Original 20 (v0.30):
  "🦊", "🐶", "🐱", "🐻", "🐼", "🐨", "🦁", "🐯", "🐸", "🐵",
  "🦉", "🐧", "🍎", "🍊", "🍋", "🍉", "🍇", "🍓", "🍍", "🥝",
  // +40 (v0.60.19):
  "🐰", "🦝", "🐺", "🦄", "🐮", "🐷", "🐹", "🐭", "🐔", "🦆",
  "🦅", "🦜", "🐢", "🦎", "🐙", "🐬", "🐳", "🦋", "🐝", "🐞",
  "🐘", "🐊", "🦒", "🦓", "🦥", "🦔", "🐴", "🐑", "🦈", "🐠",
  "🍒", "🥭", "🥕", "🌵", "🍀", "🌻", "🌈", "⭐", "🔥", "🚀",
  // ---- end of DEFAULT_POOL (60) ---- pick-only from here (v0.60.20, +40):
  "🌹", "🌼", "🌷", "🪷", "🦭", "🦞", "🐪", "🦍", "🦧", "🙈",
  "🙉", "🙊", "🐓", "🦩", "🕊️", "🦚", "🐉", "🐲", "⚽", "🏀",
  "🏉", "🎾", "🎸", "🏍️", "✈️", "🛸", "🗝️", "🧸", "🏁", "🏎️",
  "🎩", "🧢", "👑", "👒", "👛", "👜", "🎒", "🧳", "🫆", "✨",
];

/** Size of the default-assignment pool (the first N entries). NEVER change. */
export const DEFAULT_POOL = 60;

const OVERRIDE_KEY = "m.emoji";

// FNV-1a-ish: a tiny, dependency-free hash so the same string always lands on
// the same slot, identically on every browser.
function stableIndex(input: string, n: number): number {
  let h = 0x811c9dc5;
  // Normalise so "Crackly Snipe 7926" and "crackly-snipe-7926" hash the same —
  // the login screen and the in-app panel must always agree on the emoji.
  const s = input.toLowerCase().replace(/[^a-z0-9]/g, "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % n;
}

export function defaultEmoji(key: string): string {
  return EMOJI_SET[stableIndex(key || "m", DEFAULT_POOL)]; // v0.60.20: fixed pool, not the whole set
}

// The emoji to show: the device override if the user picked one, else the
// name-derived default. One wallet per browser, so a single override key is fine.
export function getEmoji(key: string): string {
  try {
    const o = localStorage.getItem(OVERRIDE_KEY);
    if (o && EMOJI_SET.includes(o)) return o;
  } catch {
    /* storage blocked — fall through to the default */
  }
  return defaultEmoji(key);
}

/** v0.70.5 — the device pick, if any (for the one-time server sync). */
export function getEmojiOverride(): string | null {
  try {
    const o = localStorage.getItem(OVERRIDE_KEY);
    return o && EMOJI_SET.includes(o) ? o : null;
  } catch {
    return null;
  }
}

/** v0.70 — someone else's emoji: their server-stored pick if any, else the
 *  hash of their code. Same rule the login button uses for your own. */
export function emojiFor(code: string | null, override?: string | null): string {
  if (override && EMOJI_SET.includes(override)) return override;
  return defaultEmoji(code ?? "m");
}

export function setEmoji(emoji: string): void {
  try {
    localStorage.setItem(OVERRIDE_KEY, emoji);
    void import("./settings").then((m) => m.touch("emoji")); // v0.80.40
  } catch {
    /* ignore — cosmetic only */
  }
}
